import React, { useState } from 'react';
import { Button, Dropdown, Tooltip, theme } from 'antd';
import { CloudServerOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { projectRoster } from './staffPresenceModel';
import AppTheme from '../theme/AppTheme';
import { StaffAvatar } from '../staff/StaffAvatar';
import uzdLogo from '../../assets/images/uzd_logo_small.jpeg';
import './staff-presence.css';
import { useAccess } from '../../features/access/context/AccessProvider';

const HRM = React.lazy(() => import('../../pages/HRM'));

const labels = { online: 'Online', offline: 'Offline', unknown: 'Unknown' };
const Presence = ({ value, platform }) => <Tooltip title={`${platform}: ${labels[value]}`}>
  <span className={`staff-presence-platform is-${value}`} aria-label={`${platform}: ${labels[value]}`}>
    {platform === 'SRM' ? <img src={uzdLogo} alt="" /> : <WhatsAppOutlined aria-hidden="true" />}
    <i aria-hidden="true" />
  </span>
</Tooltip>;

function StaffPresenceHeaderContent({ connected, networkOnline, snapshot = null, roster: liveRoster, error, open, onOpenChange }) {
  const { can } = useAccess();
  const canOpenStaff = can('staff_presence.notifications') && can('users.view');
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const { token } = theme.useToken();
  const roster = liveRoster || projectRoster(snapshot);
  const serverOnline = connected && networkOnline;
  const content = <div className="staff-presence-dropdown" style={{ background: token.colorBgElevated, color: token.colorText, boxShadow: token.boxShadowSecondary, border: `1px solid ${token.colorBorderSecondary}`, '--presence-surface': token.colorBgElevated, '--presence-muted': token.colorTextSecondary, '--presence-border': token.colorBorderSecondary }}>
    <strong>Staff presence</strong>
    {!snapshot && !liveRoster ? <p>{error || 'Loading staff presence…'}</p> : roster.groups.map(group => <section key={group.mode}>
      <h4>{group.mode === 'shared' ? 'Shared account users' : 'Individual account users'}</h4>
      {!group.rows.length && <p>No users</p>}
      {group.rows.map(row => <div className={`staff-presence-row${canOpenStaff ? ' is-clickable' : ''}`} key={row.userId}
        role={canOpenStaff ? 'button' : undefined} tabIndex={canOpenStaff ? 0 : undefined}
        aria-label={canOpenStaff ? `Open staff profile: ${row.displayName}` : undefined}
        onClick={canOpenStaff ? () => { setSelectedStaffId(row.userId); onOpenChange?.(false); } : undefined}
        onKeyDown={canOpenStaff ? event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelectedStaffId(row.userId); onOpenChange?.(false); } } : undefined}>
        <StaffAvatar size={32} userId={row.userId} name={row.displayName} />
        <strong className="staff-presence-name" title={row.displayName}>{row.displayName}</strong>
        <div className="staff-presence-platforms"><Presence value={row.srmPresence} platform="SRM" /><Presence value={row.watiPresence} platform="WATI" /></div>
      </div>)}
    </section>)}
  </div>;
  return <>
    {canOpenStaff && selectedStaffId != null && <React.Suspense fallback={null}><HRM key={selectedStaffId} embeddedUserId={selectedStaffId} onEmbeddedClose={() => setSelectedStaffId(null)} /></React.Suspense>}
    <Tooltip title={serverOnline ? 'Connected to Uzdpart servers' : 'Disconnected from Uzdpart servers'}>
      <Button className={`app-header-icon-button staff-presence-icon is-${serverOnline ? 'online' : 'offline'}`} aria-label="SRM socket connection" icon={<CloudServerOutlined />} />
    </Tooltip>
    <Dropdown trigger={['click']} open={open} onOpenChange={onOpenChange} popupRender={() => content}>
      <Tooltip title="WATI account presence">
        <Button className={`app-header-icon-button staff-presence-icon is-${roster.effectivePresence}`} aria-label="WATI account presence" icon={<WhatsAppOutlined />} />
      </Tooltip>
    </Dropdown>
  </>;
}

export default function StaffPresenceHeader(props) {
  return <AppTheme><StaffPresenceHeaderContent {...props} /></AppTheme>;
}
